import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { H2, P, UL, CONTACT_EMAIL, COMPANY_LEGAL_NAME } from './LegalPage';

// Public privacy policy for simplicitydynamics.ca, SimpleOffice (office web
// application) and SimpleWork (mobile application). Written at category
// level, in the conventional numbered structure of B2B software privacy
// policies - no product walk-throughs or feature examples (owner 2026-10-07).
// Section 7 carries the clause Brevo requires verbatim for the Canada / USA
// toll-free SMS registration - keep that sentence exactly as written.
// 2026-10-10: reviewed against the PIPEDA fair information principles and
// Alberta PIPA (s.6, s.13.1, s.34.1): added breach handling, accuracy, the
// outside-Canada service-provider notice, access/complaint handling and the
// Customer's notice-and-consent responsibility.
const EFFECTIVE = 'October 11, 2026';

const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" effective={EFFECTIVE}>
    <P>
      {COMPANY_LEGAL_NAME} (&ldquo;Simplicity Dynamics&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or
      &ldquo;our&rdquo;) respects your privacy. This Privacy Policy describes how we collect, use, disclose
      and protect personal information in connection with our websites, our web and mobile applications,
      and the related services we provide (collectively, the &ldquo;Services&rdquo;). It also describes the
      choices available to you regarding our use of your personal information and how you can contact us.
    </P>
    <P>
      We are a Canadian company headquartered in Calgary, Alberta. We handle personal information in
      accordance with the Personal Information Protection and Electronic Documents Act (Canada), the
      Personal Information Protection Act (Alberta) and other applicable privacy laws.
    </P>

    <H2>1. Scope</H2>
    <P>
      This Policy applies to personal information we process on our own behalf, for example when you visit
      our website, request information, create an account or contact us.
    </P>
    <P>
      Our Services are provided to businesses (&ldquo;Customers&rdquo;). When a Customer uses the Services
      to manage its own workforce and operations, the Customer determines what information is collected
      and how it is used, and we process that information on the Customer&rsquo;s behalf and under its
      instructions. This Policy does not govern a Customer&rsquo;s own privacy practices. If you are an
      employee or contractor of a Customer, please refer to your employer&rsquo;s privacy notice and
      direct any questions about your information to your employer in the first instance. Customers
      are responsible for providing any notices to, and obtaining any consents from, their personnel
      that applicable law requires for their use of the Services.
    </P>

    <H2>2. Personal Information We Collect</H2>
    <P>We may collect the following categories of personal information:</P>
    <UL
      items={[
        'Identity and contact information, such as name, business email address, telephone number, mailing address, job title and employer.',
        'Account and authentication information, such as usernames, access roles, permissions and security credentials (passwords are handled by our authentication provider and are not visible to us).',
        'Workforce and employment records entered into the Services by a Customer, which may include dates of birth, pay rates, certifications and training records, emergency contacts, and electronic signatures.',
        'Location information collected through the mobile application while time tracking is active, and location information reported by telematics devices that a Customer installs in its vehicles and equipment.',
        'Operational records created through the Services, such as time and attendance records, schedules, inspections, safety and compliance documentation, incident reports, project documents, drawings and progress records, inventory and expense records, and photographs or videos uploaded by users.',
        'Communications, including messages sent through the Services, support requests and correspondence with us.',
        'Device and technical information, such as device identifiers, notification tokens, operating system and application version, IP address, browser type, and logs of activity within the Services.',
        'Website information, such as the pages you visit and the general region you connect from, collected through standard server logs.',
      ]}
    />
    <P>
      We do not collect or store full payment card numbers. Where fees are paid by card, payments are
      processed by a third-party payment processor, and we receive only confirmation details.
    </P>
    <P>
      We limit the personal information we collect on our own behalf to what is reasonably needed for
      the purposes described in this Policy, and we collect it by fair and lawful means.
    </P>

    <H2>3. How We Collect Personal Information</H2>
    <UL
      items={[
        'Directly from you, when you create or administer an account, enter information into the Services, or communicate with us.',
        'From our Customers, when they add their personnel to the Services or configure features on their behalf.',
        'Automatically, through the Services and the devices on which they run, including the mobile application and telematics devices connected to a Customer’s account.',
        'From third parties that a Customer chooses to connect to the Services, such as project-management or document platforms.',
      ]}
    />

    <H2>4. How We Use Personal Information</H2>
    <P>We may use personal information for the following purposes:</P>
    <UL
      items={[
        'To provide, operate, maintain and support the Services.',
        'To authenticate users, administer accounts and enforce access permissions.',
        'To deliver notifications and communications that you or your Customer have enabled, by email, push notification or text message.',
        'To generate reports, documents and records requested through the Services.',
        'To provide customer support and respond to inquiries.',
        'To monitor, secure and improve the Services, including troubleshooting and the prevention of fraud, abuse and unauthorized access; where practicable, improvement uses aggregated or de-identified information.',
        'To comply with legal obligations and to establish, exercise or defend legal claims.',
        'For any other purpose disclosed to you at the time of collection or with your consent.',
      ]}
    />
    <P>
      Personal information that a Customer enters into the Services is processed only as needed to
      provide the Services to that Customer and for the related security, support and legal purposes
      described above. We do not use it for our own purposes.
    </P>
    <P>
      Certain features of the Services may use artificial-intelligence technologies provided by third
      parties to process content submitted by users. Such content is processed to deliver the requested
      feature and, under the terms of our agreements with those providers, is not used to train their
      models.
    </P>

    <H2>5. Disclosure of Personal Information</H2>
    <P>We do not sell personal information. We may disclose personal information as follows:</P>
    <UL
      items={[
        'To the Customer whose account the information belongs to, including its authorized administrators and personnel, in accordance with the permissions the Customer configures.',
        'To service providers that perform functions on our behalf, such as cloud hosting and storage, authentication, communications delivery (email, push and text messaging), mapping, artificial-intelligence processing, analytics and payment processing. These providers are contractually required to protect personal information and to use it only for the services they provide to us.',
        'To third-party platforms that a Customer elects to connect to the Services, to the extent necessary to provide the integration the Customer has enabled.',
        'To recipients designated by a Customer or user, for example when a document or report is sent from the Services to another party.',
        'To comply with applicable law, regulation, legal process or governmental request, or to protect the rights, property or safety of Simplicity Dynamics, our Customers, users or the public.',
        'In connection with a merger, acquisition, financing, reorganization or sale of all or part of our business, subject to appropriate confidentiality protections.',
      ]}
    />

    <H2>6. International Transfers</H2>
    <P>
      The Services are hosted by third-party cloud providers with data centres located in the United
      States. Personal information may therefore be stored and processed outside of Canada and may be
      subject to the laws of, and access by authorities in, those jurisdictions. Service providers
      outside Canada are authorized to collect, use, disclose or store personal information only for
      the purpose of hosting, delivering and supporting the Services on our behalf. We use contractual,
      organizational and technical safeguards designed to protect personal information wherever it is
      processed. Written information about our policies and practices with respect to service providers
      outside Canada is available on request, and our Privacy Officer (Section 15) can answer questions
      about the collection, use, disclosure or storage of personal information by those providers.
    </P>

    <H2>7. Text Messaging</H2>
    <P>
      Where text-message notifications are enabled, we may send informational (non-marketing) text
      messages to the mobile telephone numbers provided to us. By providing a mobile number and enabling
      text-message notifications, you consent to receive such messages. Message frequency varies. Message
      and data rates may apply. You may opt out at any time by replying STOP to any message, and you may
      reply HELP for assistance. Carriers are not liable for delayed or undelivered messages.
    </P>
    <P>
      All the above categories exclude text messaging originator opt-in data and consent; this information
      won&rsquo;t be shared with any third parties.
    </P>

    <H2>8. Cookies and Similar Technologies</H2>
    <P>
      The Services use cookies and similar technologies that are strictly necessary to keep you signed in,
      to remember your preferences and to secure the Services. We do not use advertising cookies or
      third-party advertising trackers on our website. You can control cookies through your browser
      settings; disabling necessary cookies may affect the functioning of the Services.
    </P>
    <P>
      Our website and web applications use Google reCAPTCHA to protect forms and requests against
      automated abuse. reCAPTCHA may set cookies and collects device and interaction information,
      which is sent to Google and is subject to the Google Privacy Policy and Terms of Service. We do
      not use this information for any other purpose.
    </P>

    <H2>9. Data Retention</H2>
    <P>
      We retain personal information for as long as necessary to fulfil the purposes described in this
      Policy, for the duration of the Customer relationship and for the export period set out in our Terms
      &amp; Conditions thereafter to allow the Customer to retrieve its records, and as required to comply
      with legal, regulatory, accounting or reporting obligations. Retention periods for workforce and
      safety records are determined by the Customer in accordance with its own legal obligations. When we
      no longer reasonably require personal information for these purposes, we securely delete it or
      render it non-identifying. Backup copies are retained for a limited period and then overwritten in
      the ordinary course.
    </P>

    <H2>10. Security</H2>
    <P>
      We maintain administrative, technical and physical safeguards appropriate to the sensitivity of the
      information we hold, including encryption of data in transit and at rest, logical separation of each
      Customer&rsquo;s data, role-based access controls, restricted access to stored files, security
      testing and regular backups. No method of transmission or storage is completely secure, and we
      cannot guarantee absolute security.
    </P>
    <P>
      We maintain procedures to identify, contain and respond to security incidents and we keep records
      of them. Where an incident involves personal information we process on behalf of a Customer, we
      will notify that Customer without undue delay and assist it in meeting its own notification
      obligations. Where we are responsible for the personal information, we will notify affected
      individuals and the relevant privacy regulators as required by law.
    </P>

    <H2>11. Your Rights and Choices</H2>
    <UL
      items={[
        'Access and correction: subject to applicable law, you may request access to the personal information we hold about you, to be told how it is used and to whom it has been disclosed, and to have inaccurate information corrected. We respond within the time required by applicable law and at no or minimal cost. Personnel of a Customer should direct such requests to their employer, which maintains their records; where we receive such a request for information we process on behalf of a Customer, we may refer it to the Customer and will reasonably assist the Customer in responding.',
        'Accuracy: we take reasonable steps to keep the personal information we hold on our own behalf accurate, complete and up to date. Customers are responsible for the accuracy of the records they enter into the Services, and users may correct their own records through the Services or through their employer.',
        'Withdrawal of consent: where processing is based on consent, you may withdraw it at any time, subject to legal or contractual restrictions and reasonable notice. Withdrawal may limit the Services available to you.',
        'Notifications: push notifications may be managed through your device settings; email and text-message notifications are managed through the Services or by contacting your employer or us.',
        'Deletion: a Customer may delete records within the Services and may request deletion of its account. We delete or de-identify personal information in accordance with Section 9.',
        'Complaints: you may raise a concern about our handling of personal information with our Privacy Officer (Section 15). We investigate every complaint and respond to the person who made it. If you are not satisfied with our response, you may contact the Office of the Privacy Commissioner of Canada or the Office of the Information and Privacy Commissioner of Alberta.',
      ]}
    />

    <H2>12. Children&rsquo;s Privacy</H2>
    <P>
      The Services are intended for business use and are not directed to individuals under the age of 16.
      We do not knowingly collect personal information from children.
    </P>

    <H2>13. Third-Party Links and Services</H2>
    <P>
      The Services may contain links to, or integrations with, third-party websites and services. This
      Policy does not apply to those third parties, and we encourage you to review their privacy policies.
    </P>

    <H2>14. Changes to this Policy</H2>
    <P>
      We may update this Policy from time to time. We will post the revised Policy on this page with a new
      effective date and, where changes are material, notify our Customers in advance by email or through
      the Services.
    </P>

    <H2>15. Contact Us</H2>
    <P>
      Questions, requests or complaints regarding this Policy or our privacy practices may be directed to
      our Privacy Officer at {COMPANY_LEGAL_NAME}, Calgary, Alberta, Canada, by email at{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-[#2f66b8] hover:underline">
        {CONTACT_EMAIL}
      </a>
      . Our Privacy Officer is accountable for our compliance with this Policy, including for personal
      information we transfer to service providers. Written information about our privacy policies and
      practices is available on request. Please also review our{' '}
      <Link to="/terms" className="font-medium text-[#2f66b8] hover:underline">
        Terms &amp; Conditions
      </Link>
      .
    </P>
  </LegalPage>
);

export default PrivacyPolicy;
